import type { UnknownAction } from "@reduxjs/toolkit";
import { GET_ERRORS } from "./errorServices/errorTypes";

/**
 * SCRUM-840 Part 3 — the read-thunk safety net.
 *
 * Many read thunks fetch-and-forget with no `.catch`; because the axios
 * interceptor (`api.js`) rejects every non-401 response, a failed read becomes
 * an unhandled promise rejection (console noise, and the user never learns the
 * fetch failed). A failed read is lower severity than a failed write — an empty
 * list, not a false success — so rather than hand-catch each read, one global
 * `unhandledrejection` listener routes any axios-like rejection to the same
 * `GET_ERRORS` → `errors` slice → `GlobalNotifications` hub. Reads get properly
 * absorbed later under RTK Query; this removes the silent failure meanwhile.
 */

/**
 * The fields of a rejection reason this net reads. A reason is whatever was
 * thrown, so every field is optional and the reason itself may be missing.
 */
type RejectionReason =
  | {
      response?: { data?: unknown };
      message?: string;
      isAxiosError?: boolean;
      config?: unknown;
    }
  | null
  | undefined;

type Dispatch = (action: UnknownAction) => void;

/**
 * Pull a hub-displayable payload out of an axios-style rejection reason.
 */
export const toDisplayableError = (reason: RejectionReason): unknown => {
  if (reason?.response?.data) return reason.response.data;
  if (reason?.message) return { message: reason.message };
  return null;
};

/**
 * True only for rejections that look like an axios/HTTP failure we want to
 * surface — so unrelated library rejections are left alone.
 */
export const isAxiosLikeRejection = (reason: RejectionReason) =>
  !!(reason && (reason.isAxiosError || reason.response || reason.config));

export const makeUnhandledRejectionHandler =
  (dispatch: Dispatch) =>
  (event: PromiseRejectionEvent): void => {
  const reason = event?.reason;
  if (!isAxiosLikeRejection(reason)) return;
  const payload = toDisplayableError(reason);
  if (!payload) return;
  dispatch({ type: GET_ERRORS, payload });
  // We surfaced it to the user; silence the default console noise.
  event?.preventDefault?.();
};

/**
 * Register the net once, at app start. Returns an unregister function.
 */
export const registerGlobalRejectionNet = (store: { dispatch: Dispatch }): (() => void) => {
  if (typeof window === "undefined") return () => {};
  const handler = makeUnhandledRejectionHandler(store.dispatch);
  window.addEventListener("unhandledrejection", handler);
  return () => window.removeEventListener("unhandledrejection", handler);
};

